import React, { useState } from 'react';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';

const EXPERIENCE_LEVELS = [
  { value: 'beginner', label: 'Beginner', icon: 'Sprout', desc: 'Just starting my investment journey', color: '#059669' },
  { value: 'intermediate', label: 'Intermediate', icon: 'TrendingUp', desc: '1-5 years of investing experience', color: '#2D4A87' },
  { value: 'advanced', label: 'Advanced', icon: 'Award', desc: '5+ years, diverse portfolio experience', color: '#D4A520' },
];

const INVESTMENT_GOALS = [
  { id: 'retirement', label: 'Retirement Planning', icon: 'Sunset' },
  { id: 'wealth', label: 'Wealth Building', icon: 'TrendingUp' },
  { id: 'education', label: 'Education Fund', icon: 'BookOpen' },
  { id: 'realestate', label: 'Real Estate', icon: 'Home' },
  { id: 'business', label: 'Business Investment', icon: 'Briefcase' },
  { id: 'passive', label: 'Passive Income', icon: 'DollarSign' },
  { id: 'emergency', label: 'Emergency Fund', icon: 'Shield' },
  { id: 'travel', label: 'Travel & Lifestyle', icon: 'Globe' },
];

const MEMBERSHIP_TIERS = [
  {
    id: 'seed', name: 'Seed', price: 19, icon: 'Sprout', color: '#059669',
    features: ['Access to community hub', 'Basic investment education', 'Monthly newsletter', 'Marketplace browsing', '2 community rooms'],
    badge: null,
  },
  {
    id: 'growth', name: 'Growth', price: 49, icon: 'TrendingUp', color: '#2D4A87',
    features: ['Everything in Seed', 'AI Investment Assistant', 'Portfolio tracker', 'All community rooms', 'Monthly expert office hours', 'WIN Academy courses'],
    badge: 'Most Popular',
  },
  {
    id: 'pinnacle', name: 'Pinnacle', price: 99, icon: 'Crown', color: '#D4A520',
    features: ['Everything in Growth', 'Exclusive deal flow', 'Physical WIN coin keepsake', 'Priority mentor matching', 'Founding Member badge', 'VIP event access', 'Direct founder connections'],
    badge: 'Best Value',
  },
];

export default function WelcomeWizard({ userData, onComplete }) {
  const [step, setStep] = useState(1);
  const [selectedLevel, setSelectedLevel] = useState(userData?.experience || '');
  const [selectedGoals, setSelectedGoals] = useState([]);
  const [selectedTier, setSelectedTier] = useState('growth');

  const toggleGoal = (id) => {
    setSelectedGoals(prev => prev?.includes(id) ? prev?.filter(g => g !== id) : [...prev, id]);
  };

  const handleNext = () => {
    if (step < 3) setStep(s => s + 1);
    else onComplete({ level: selectedLevel, goals: selectedGoals, tier: selectedTier });
  };

  const canProceed = () => {
    if (step === 1) return !!selectedLevel;
    if (step === 2) return selectedGoals?.length > 0;
    return true;
  };

  return (
    <div className="w-full">
      {/* Progress */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          {[1, 2, 3]?.map(s => (
            <React.Fragment key={s}>
              <div className="flex flex-col items-center gap-1">
                <div className="rounded-full flex items-center justify-center transition-all"
                  style={{
                    width: 36, height: 36,
                    background: s <= step ? 'var(--color-accent)' : 'var(--color-muted)',
                    color: s <= step ? 'var(--color-primary)' : 'var(--color-muted-foreground)',
                    fontFamily: 'var(--font-caption)', fontWeight: 700, fontSize: '0.875rem',
                  }}>
                  {s < step ? <Icon name="Check" size={16} color="var(--color-primary)" /> : s}
                </div>
                <span style={{ fontFamily: 'var(--font-caption)', fontSize: '0.7rem', color: s === step ? 'var(--color-accent)' : 'var(--color-muted-foreground)', whiteSpace: 'nowrap' }}>
                  {s === 1 ? 'Experience' : s === 2 ? 'Goals' : 'Membership'}
                </span>
              </div>
              {s < 3 && <div className="flex-1 h-0.5 mx-2 mb-4 transition-all" style={{ background: s < step ? 'var(--color-accent)' : 'var(--color-muted)' }} />}
            </React.Fragment>
          ))}
        </div>
      </div>
      {/* Step 1: Experience Level */}
      {step === 1 && (
        <div>
          <h2 className="mb-1" style={{ fontFamily: 'var(--font-heading)', fontSize: '1.5rem', color: 'var(--color-foreground)' }}>
            What&apos;s your investment experience?
          </h2>
          <p className="mb-5" style={{ fontFamily: 'var(--font-caption)', color: 'var(--color-muted-foreground)', fontSize: '0.875rem' }}>
            We&apos;ll personalize your WIN experience based on your level
          </p>
          <div className="flex flex-col gap-3">
            {EXPERIENCE_LEVELS?.map(level => (
              <button key={level?.value} type="button"
                className="flex items-center gap-4 rounded-xl p-4 transition-all text-left w-full"
                style={{
                  border: `2px solid ${selectedLevel === level?.value ? 'var(--color-accent)' : 'var(--color-border)'}`,
                  background: selectedLevel === level?.value ? 'rgba(133,113,77,0.06)' : 'var(--color-surface)',
                  cursor: 'pointer',
                }}
                onClick={() => setSelectedLevel(level?.value)}>
                <div className="rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ width: 44, height: 44, background: `${level?.color}18` }}>
                  <Icon name={level?.icon} size={22} color={level?.color} />
                </div>
                <div className="flex-1">
                  <div style={{ fontFamily: 'var(--font-caption)', fontWeight: 600, fontSize: '0.9375rem', color: 'var(--color-foreground)' }}>{level?.label}</div>
                  <div style={{ fontFamily: 'var(--font-caption)', fontSize: '0.8125rem', color: 'var(--color-muted-foreground)' }}>{level?.desc}</div>
                </div>
                {selectedLevel === level?.value && <Icon name="CheckCircle" size={20} color="var(--color-accent)" />}
              </button>
            ))}
          </div>
        </div>
      )}
      {/* Step 2: Investment Goals */}
      {step === 2 && (
        <div>
          <h2 className="mb-1" style={{ fontFamily: 'var(--font-heading)', fontSize: '1.5rem', color: 'var(--color-foreground)' }}>
            What are your investment goals?
          </h2>
          <p className="mb-5" style={{ fontFamily: 'var(--font-caption)', color: 'var(--color-muted-foreground)', fontSize: '0.875rem' }}>
            Select all that apply — we&apos;ll curate opportunities for you
          </p>
          <div className="grid grid-cols-2 gap-3">
            {INVESTMENT_GOALS?.map(goal => {
              const selected = selectedGoals?.includes(goal?.id);
              return (
                <button key={goal?.id} type="button"
                  className="flex items-center gap-3 rounded-xl p-3 transition-all text-left"
                  style={{
                    border: `2px solid ${selected ? 'var(--color-accent)' : 'var(--color-border)'}`,
                    background: selected ? 'rgba(133,113,77,0.06)' : 'var(--color-surface)',
                    cursor: 'pointer',
                  }}
                  onClick={() => toggleGoal(goal?.id)}>
                  <div className="rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ width: 36, height: 36, background: selected ? 'rgba(133,113,77,0.15)' : 'var(--color-muted)' }}>
                    <Icon name={goal?.icon} size={18} color={selected ? 'var(--color-accent)' : 'var(--color-muted-foreground)'} />
                  </div>
                  <span style={{ fontFamily: 'var(--font-caption)', fontSize: '0.8125rem', fontWeight: selected ? 600 : 400, color: 'var(--color-foreground)', lineHeight: 1.3 }}>
                    {goal?.label}
                  </span>
                </button>
              );
            })}
          </div>
          {selectedGoals?.length > 0 && (
            <p className="mt-3" style={{ fontFamily: 'var(--font-caption)', fontSize: '0.8125rem', color: 'var(--color-accent)' }}>
              {selectedGoals?.length} goal{selectedGoals?.length > 1 ? 's' : ''} selected
            </p>
          )}
        </div>
      )}
      {/* Step 3: Membership Tier */}
      {step === 3 && (
        <div>
          <h2 className="mb-1" style={{ fontFamily: 'var(--font-heading)', fontSize: '1.5rem', color: 'var(--color-foreground)' }}>
            Choose your membership
          </h2>
          <p className="mb-4" style={{ fontFamily: 'var(--font-caption)', color: 'var(--color-muted-foreground)', fontSize: '0.875rem' }}>
            Start free for 14 days, cancel anytime
          </p>

          {/* Founding Member Banner */}
          <div className="rounded-lg p-3 mb-4 flex items-center gap-3"
            style={{ background: 'linear-gradient(135deg, rgba(0,48,87,0.08), rgba(133,113,77,0.12))', border: '1px solid rgba(133,113,77,0.4)' }}>
            <Icon name="Crown" size={18} color="var(--color-accent)" />
            <div>
              <div style={{ fontFamily: 'var(--font-caption)', fontWeight: 700, fontSize: '0.8125rem', color: 'var(--color-foreground)' }}>
                🎉 Founding Member Program
              </div>
              <div style={{ fontFamily: 'var(--font-caption)', fontSize: '0.75rem', color: 'var(--color-muted-foreground)' }}>
                First 500 members receive a physical WIN gold coin keepsake
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-3">
            {MEMBERSHIP_TIERS?.map(tier => (
              <button key={tier?.id} type="button"
                className="rounded-xl p-4 transition-all text-left w-full relative"
                style={{
                  border: `2px solid ${selectedTier === tier?.id ? tier?.color : 'var(--color-border)'}`,
                  background: selectedTier === tier?.id ? `${tier?.color}08` : 'var(--color-surface)',
                  cursor: 'pointer',
                }}
                onClick={() => setSelectedTier(tier?.id)}>
                {tier?.badge && (
                  <div className="absolute -top-2.5 right-4 rounded-full px-3 py-0.5"
                    style={{ background: tier?.color, fontFamily: 'var(--font-caption)', fontSize: '0.6875rem', fontWeight: 700, color: tier?.id === 'pinnacle' ? '#0B1F3B' : '#FFFFFF' }}>
                    {tier?.badge}
                  </div>
                )}
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <div className="rounded-lg flex items-center justify-center"
                      style={{ width: 36, height: 36, background: `${tier?.color}18` }}>
                      <Icon name={tier?.icon} size={18} color={tier?.color} />
                    </div>
                    <div>
                      <div style={{ fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: '1.125rem', color: 'var(--color-foreground)' }}>{tier?.name}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <span style={{ fontFamily: 'var(--font-data)', fontWeight: 700, fontSize: '1.25rem', color: tier?.color }}>${tier?.price}</span>
                    <span style={{ fontFamily: 'var(--font-caption)', fontSize: '0.75rem', color: 'var(--color-muted-foreground)' }}>/mo</span>
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-1">
                  {tier?.features?.slice(0, 3)?.map((f, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <Icon name="Check" size={13} color={tier?.color} />
                      <span style={{ fontFamily: 'var(--font-caption)', fontSize: '0.8rem', color: 'var(--color-muted-foreground)' }}>{f}</span>
                    </div>
                  ))}
                  {tier?.features?.length > 3 && (
                    <span style={{ fontFamily: 'var(--font-caption)', fontSize: '0.75rem', color: tier?.color, marginLeft: 21 }}>
                      +{tier?.features?.length - 3} more benefits
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
      {/* Navigation */}
      <div className="flex gap-3 mt-6">
        {step > 1 && (
          <Button variant="outline" onClick={() => setStep(s => s - 1)} className="flex-1">
            Back
          </Button>
        )}
        <Button variant="default" onClick={handleNext} disabled={!canProceed()}
          className={step === 1 ? 'w-full' : 'flex-1'}
          iconName={step === 3 ? 'ArrowRight' : undefined} iconPosition="right">
          {step === 3 ? 'Start My Journey' : 'Continue'}
        </Button>
      </div>
    </div>
  );
}